/**
 * #1070 — Unit Grouping (toolkit `clustering` parity), consumer for central#3210.
 *
 * Central collapses identical units into one representative card
 * (`GET /public-api/v1/{feed}/feed/properties`). A representative row carries
 * `unit_group: { key, size, sibling_ids, sibling_slugs }`; every other row
 * carries `unit_group: null`. The toolkit's own behavior (Clustering.php /
 * ResponseFilter) is: the card stays visually stable (rep's title/photo), but
 * each CLICK resolves to a random available sibling so the reservation binds
 * to one physical unit. This module is the one place that random pick happens
 * so every card renderer (TemplateExplorer's legacy .hfse-* card and the
 * hr-pc-* template family via explorer-template-bridge/card-parts) makes the
 * same decision the same way.
 */

/** The central#3210 payload shape, present only on a group's representative row. */
export type UnitGroup = {
  key: string
  size: number
  sibling_ids: string[]
  sibling_slugs: (string | null)[]
}

/**
 * Uniformly pick one slug from `sibling_slugs ∪ own slug` (#1070 brief,
 * intentionally a union — whether or not Central's `sibling_slugs` includes
 * the representative's own entry, a Set dedupes it either way). Called at
 * CLICK TIME, not render time, so repeat clicks on the same card distribute
 * across the whole physical group — matching the toolkit's per-click
 * resolution instead of pinning one visitor to one unit for the whole
 * session.
 *
 * `unit_group` absent/null (ungrouped, or a sibling row) returns the
 * property's own slug unchanged — every existing card keeps its exact
 * current click target with zero behavior change.
 *
 * `rng` is injectable (defaults to `Math.random`) so callers can pin the
 * selection in tests without mocking the global.
 */
export function pickUnitGroupSlug(
  property: { slug: string | null; unit_group?: UnitGroup | null },
  rng: () => number = Math.random,
): string | null {
  const group = property.unit_group
  if (!group) return property.slug

  const siblings = Array.isArray(group.sibling_slugs) ? group.sibling_slugs : []
  const candidates = Array.from(
    new Set(
      [property.slug, ...siblings].filter(
        (slug): slug is string => typeof slug === 'string' && slug.length > 0,
      ),
    ),
  )
  if (candidates.length === 0) return property.slug

  const index = Math.floor(rng() * candidates.length)
  // Guard the pathological rng() === 1 edge (Math.random() never returns 1,
  // but an injected test rng might) rather than reading past the array end.
  return candidates[Math.min(index, candidates.length - 1)]
}
